import { CanvasHandler } from "../../handlers";
import { FabricEvent, FabricObject } from "../../types/FabricCustomType";
import { IObjectBlocker } from "../../types/HandlerObjectType";
import { getSvgObject, updateObjectStatusInDb, createObjectModel } from "../../utils/ObjectUtil";
import { TooltipSeatBlocker } from "../../components/tooltip/TooltipSeatBlocker";
import { isTouchDevice, isTouchInteraction } from "../../utils/InteractionUtil";
import { IconKey, SeatIcons } from "../../enums/SeatIcon";

abstract class ObjectBlocker implements IObjectBlocker {
  protected canvasHandler: CanvasHandler;
  private targetObject!: FabricObject<Object> | null;
  private forSale: any[];
  private noForSale: any[];
  private hoverRadius: number | null = null;

  protected abstract getIconSize(object: any): number;
  protected abstract getSelectionTargets(group: any): FabricObject<Object>[];

  protected getHoverRadius(object: any): number | null { return null; }
  protected hasSpecialIcon(object: any): boolean { return false; }
  protected restoreSpecialIcon(object: any): void {}

  constructor(canvasHandler: CanvasHandler) {
    this.canvasHandler = canvasHandler;
    this.forSale = [];
    this.noForSale = [];
  }

  public activateObjectHover(event: any, object: FabricObject<Object>): any {
    if (this.targetObject != object) {
      this.deactivateObjectHover();//Elimina el hover del objeto anterior

      const objectShape = object.item(0);
      this.hoverRadius = this.getHoverRadius(object);
      this.setTooltip(event, object);

      if ((object.status == "Free" || object.status == "NoForSale") && !object.isSelected) {
        objectShape.set({
          fill: `${object.statusColor[object.status]}90`,
          stroke: object.statusColor[object.status],
        });
      }
      else if ((object.status == "Free" || object.status == "NoForSale") && object.isSelected) {
        objectShape.set({
          fill: object.statusColor[object.status],
          stroke: object.statusColor[object.status],
        });
      }
      if (this.hoverRadius !== null && (object.status === "Free" || object.status === "NoForSale")) {
        objectShape.set({ radius: this.hoverRadius + 0.5 });
      }
      this.targetObject = object;
      this.canvasHandler.canvas.requestRenderAll();
    }

    if (this.targetObject && (this.targetObject.status == "Free" || this.targetObject.status == "NoForSale"))
      this.canvasHandler.canvas.setCursor("pointer");
    else
      this.canvasHandler.canvas.setCursor("default");
  }

  private async changeMultipleStatus(event: any): Promise<any> {
    this.canvasHandler.eventHandler.panning = false;

    let targetObjects: any[] = [];
    const selectionRect = event.selectionRect;

    if (selectionRect) {
      const { minX, maxX, minY, maxY } = selectionRect;
      const groups = this.canvasHandler.canvas.getObjects("group");
      groups.forEach((group: any) => {
        this.getSelectionTargets(group).forEach((object: any) => {
          const matrix = object.calcTransformMatrix();
          const absLeft = matrix[4];
          const absTop = matrix[5];

          // Include objects whose center falls inside the selection rectangle.
          if (absLeft >= minX && absLeft <= maxX && absTop >= minY && absTop <= maxY) {
            targetObjects.push({ object, parentKey: group.id });
          }
        });
      });
    }

    if (targetObjects.length === 0) return;

    targetObjects.forEach((item: any) => {
      this.temporalChangeStatusInCanvas(item.parentKey, item.object);
    });
    this.canvasHandler.canvas.discardActiveObject();
  }

  public async handleSelectObjects(event: FabricEvent<MouseEvent | TouchEvent>) {
    await this.changeMultipleStatus(event);
  }

  public async handleSelectObject(event: FabricEvent<MouseEvent | TouchEvent>, parentKey: string, object: FabricObject<Object>) {
    this.temporalChangeStatusInCanvas(parentKey, object);
    if (isTouchDevice()) {
      this.deactivateObjectHover();
    } else {
      this.setTooltip(event, object);
    }
  }

  public async handleObjectLockUpdate(object: any, hubItem: any, selectorKey: any): Promise<any> {
    if (object) {
      if (this.targetObject === object) this.deactivateObjectHover();
      this.forSale = this.forSale.filter(item => item.ObjectKey !== object.id);
      this.noForSale = this.noForSale.filter(item => item.ObjectKey !== object.id);
      if (hubItem.status == "Free") {
        this.releaseInCanvas(object);
      }
      else if (hubItem.status == "NoForSale") {
        this.setNoForSaleInCanvas(object);
      }
      else {
        // Reserved, Booked, Pending_Payment y cualquier otro estatus que llegue
        // por el hub se pintan con el color e icono definidos en el mapa.
        this.holdInCanvas(object, hubItem.status);
      }
    }
  }

  public deactivateObjectHover = () => {
    if (this.targetObject) {
      this.canvasHandler.tooltipHandler.hide();
      this.canvasHandler.canvas.setCursor("default");
      const objectShape = this.targetObject.item(0);
      if (this.hoverRadius !== null) objectShape.set({ radius: this.hoverRadius });
      if (this.targetObject.status == "Free" && !this.targetObject.isSelected) {
        objectShape.set({
          fill: `${this.targetObject.statusColor["Free"]}40`,
          stroke: this.canvasHandler.isInteractionEnabled ? this.targetObject.statusColor.Free : "transparent",
        });
      }
      else if (this.targetObject.status == "NoForSale" && this.targetObject.isSelected) {
        objectShape.set({
          fill: this.targetObject.statusColor["NoForSale"],
        });
      }
      else if (this.targetObject.status == "NoForSale" && !this.targetObject.isSelected) {
        const objectShape = this.targetObject.item(0);
        objectShape.set({
          fill: `${this.targetObject.statusColor["NoForSale"]}20`,
          stroke: `${this.targetObject.statusColor["NoForSale"]}20`,
        });
      }
      else if (this.targetObject.status == "Free" && this.targetObject.isSelected) {
        const objectShape = this.targetObject.item(0);
        objectShape.set({
          fill: this.targetObject.statusColor["Free"],
        });
      }
      this.canvasHandler.canvas.requestRenderAll();
      this.targetObject = null;
      this.hoverRadius = null;
    } else {
      this.canvasHandler.tooltipHandler.hide();
      this.canvasHandler.canvas.setCursor("default");
    }
  };

  private temporalChangeStatusInCanvas(parentKey: string, object: FabricObject<Object>) {
    const objectModel = createObjectModel(parentKey, object);
    const objectShape = object.item(0);
    const objectText = object.item(1);
    const hasSpecialIcon = this.hasSpecialIcon(object);

    //asiento libre y no seleccionado (se quiere bloquear)
    if (object.status == "Free" && !object.isSelected) {
      this.noForSale.push(objectModel);
      object.set({
        status: "NoForSale",
        isSelected: true
      });

      objectShape.set({
        fill: object.statusColor["NoForSale"],
        stroke: object.statusColor["NoForSale"],
      });

      const oldIcon = object.item(2);
      if (oldIcon)
        object.remove(oldIcon);

      const size = this.getIconSize(object);
      const blockIcon = getSvgObject(
        SeatIcons.x(size-3),
        size,
        "#ffffff"
      );
      blockIcon.set({
        angle: objectText.angle,
      });
      object.insertAt(blockIcon, 2, true);
      objectText.set({
        visible: false,
      });
    } else if (object.status == "NoForSale" && object.isSelected) {//asiento bloqueado y seleccionado (se quiere deshacer el bloqueo)
      this.noForSale = this.noForSale.filter(obj => objectModel.ObjectKey != obj.ObjectKey);
      object.set({
        status: "Free",
        isSelected: false,
      });
      objectShape.set({
        fill: `${object.statusColor.Free}${this.canvasHandler.isInteractionEnabled ? "40" : "80"}`,
        stroke: this.canvasHandler.isInteractionEnabled ? object.statusColor.Free : "transparent",
      });
      objectText.set({
        visible: !hasSpecialIcon && this.canvasHandler.isInteractionEnabled,
      });

      const blockIcon = object.item(2);
      if (blockIcon) {
        object.remove(blockIcon);
      }

      if (hasSpecialIcon && this.canvasHandler.isInteractionEnabled) {
        this.restoreSpecialIcon(object);
      }
    }
    else if (object.status == "NoForSale" && !object.isSelected) { //asiento bloqueado y no seleccionado (se quiere liberar)
      this.forSale.push(objectModel);
      object.set({
        status: "Free",
        isSelected: true
      });
      objectShape.set({
        fill: object.statusColor["Free"],
        stroke: object.statusColor["Free"],
      });

      const oldIcon = object.item(2);
      if (oldIcon)
        object.remove(oldIcon);

      const size = this.getIconSize(object);
      const freeIcon = getSvgObject(SeatIcons.check(size - 3), size, '#ffffff');
      freeIcon.set({
        angle: objectText.angle,
        left: 0,
        top: 0,
      });
      object.insertAt(freeIcon, 2, true);

      objectText.set({
        visible: false,
      });
    }
    else if (object.status == "Free" && object.isSelected) { //asiento libre y seleccionado (se quiere deshacer la liberacion)
      this.forSale = this.forSale.filter(object => object.ObjectKey != objectModel.ObjectKey);
      object.set({
        status: "NoForSale",
        isSelected: false
      });
      objectShape.set({
        fill: `${object.statusColor["NoForSale"]}20`,
        stroke: `${object.statusColor["NoForSale"]}20`,
      });

      const oldIcon = object.item(2);
      if (oldIcon)
        object.remove(oldIcon);

      const size = this.getIconSize(object);
      const blockIcon = getSvgObject(
        SeatIcons.x(size-3),
        size,
        object.statusColor["NoForSale"]
      );
      blockIcon.set({
        angle: objectText.angle,
      });
      object.insertAt(blockIcon, 2, true);

      objectText.set({
        visible: false,
      });
    }

    this.canvasHandler.canvas.requestRenderAll();
  }

  private async holdInCanvas(object: any, status: string) {
    const objectShape = object.item(0);
    const objectText = object.item(1);
    object.set({
      status: status,
      isSelected: false
    });
    const color = object.statusColor[status];
    objectShape.set({
      fill: `${color}20`,
      stroke: `${color}20`,
    });

    const oldIcon = object.item(2);
    if (oldIcon)
      object.remove(oldIcon);

    // Icono correspondiente al estatus (Reserved, Booked, Pending_Payment, etc.)
    // según el mapa seatStatusIcon. Se resuelve igual que en el selector.
    // El icono usa el color del estatus sin transparencia.
    const statusIcon = object.statusIcon?.[status]?.toLowerCase() as IconKey;
    if (statusIcon && statusIcon in SeatIcons) {
      const size = this.getIconSize(object);
      const icon = getSvgObject(
        SeatIcons[statusIcon](size - 3),
        size,
        color
      );
      icon.set({
        angle: objectText.angle,
        left: 0,
        top: 0,
      });
      object.insertAt(icon, 2, true);
      objectText.set({
        visible: false,
      });
    } else {
      objectText.set({
        visible: this.canvasHandler.isInteractionEnabled,
      });
    }

    this.noForSale = this.noForSale.filter(seatS => seatS.ObjectKey != object.id);
    this.canvasHandler.canvas.requestRenderAll();
  }

  private async setNoForSaleInCanvas(object: any) {
    const objectShape = object.item(0);
    const objectText = object.item(1);
    object.set({
      status: "NoForSale",
      isSelected: false
    });
    objectShape.set({
      fill: `${object.statusColor.NoForSale}20`,
      stroke: `${object.statusColor.NoForSale}20`,
    });

    const oldIcon = object.item(2);
    if (oldIcon)
      object.remove(oldIcon);

    const size = this.getIconSize(object);
    const blockIcon = getSvgObject(
      SeatIcons.x(size-3),
      size,
      object.statusColor["NoForSale"]
    );
    blockIcon.set({
      angle: objectText.angle,
      left: 0,
      top: 0
    });

    object.insertAt(blockIcon, 2, true);

    objectText.set({
      visible: false,
    });

    this.canvasHandler.canvas.requestRenderAll();
  }

  private async releaseInCanvas(object: any) {
    const objectShape = object.item(0);
    const objectText = object.item(1);
    const hasSpecialIcon = this.hasSpecialIcon(object);

    object.set({
      status: "Free",
      isSelected: false,
    });
    objectShape.set({
      fill: `${object.statusColor.Free}${this.canvasHandler.isInteractionEnabled ? "40" : "80"}`,
      stroke: this.canvasHandler.isInteractionEnabled ? object.statusColor.Free : "transparent",
    });
    objectText.set({
      visible: !hasSpecialIcon && this.canvasHandler.isInteractionEnabled,
    });

    const oldIcon = object.item(2);
    if (oldIcon) {
      object.remove(oldIcon);
    }

    if (hasSpecialIcon && this.canvasHandler.isInteractionEnabled) {
      this.restoreSpecialIcon(object);
    }

    this.canvasHandler.canvas.requestRenderAll();
  }

  private setTooltip(event: any, object: any) {
    let handleSelectObject;
    if (isTouchInteraction(event)) {
      handleSelectObject = () => this.handleSelectObject(event, event.target.id, object);
    }
    const handleClose = () => {
      this.deactivateObjectHover();
    }
    const htmlElement = TooltipSeatBlocker(object, handleSelectObject, handleClose);
    this.canvasHandler.tooltipHandler.show(event.e, htmlElement)
  }

  public async handleSaveChanges() {
    if (this.forSale.length > 0) {
      await updateObjectStatusInDb(this.forSale, "Free");
    }
    if (this.noForSale.length > 0) {
      await updateObjectStatusInDb(this.noForSale, "NoForSale");
    }
    this.forSale = [];
    this.noForSale = [];
  }
}
export default ObjectBlocker;
